import { translate as t } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LiveboardSelector } from './liveboard-selector';
import styles from './manual-translation-toggle.module.scss';
export const ManualTranslationToggle = ({ enabled, pendingValue, onToggleChange, onSave, onEdit, onCancel, isEditing, isClusterContext, clusterEnabled, learnMoreUrl, saving = false, canModify, selectedLiveboards, availableLiveboards, selectorLoading, downloading, onSelectorSearch, onSelectLiveboards, onRemoveLiveboard, onDownload, onUploadCSV, uploading = false, uploadError = null, onClearUploadError, saveError = null, downloadError = null, csvExists = null, onDownloadExistingCsv, onReplaceCsvClick, downloadingExistingCsv = false, existingCsvDownloadError = null, onDeleteCsv, deleting = false, deleteError = null, onClearDeleteError, }) => {
    const editDisabled = !canModify || saving;
    const saveDisabled = saving || pendingValue === enabled;
    const downloadDisabled = selectedLiveboards.length === 0 || downloading;
    const getUploadButtonText = () => {
        if (uploading)
            return t('adminSection.manualTranslation.uploading');
        return t('adminSection.manualTranslation.uploadButton');
    };
    const uploadButtonText = getUploadButtonText();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    useEffect(() => {
        setSelectedFile(null);
        setUploadSuccess(false);
    }, [isEditing]);
    const [selectorResetKey, setSelectorResetKey] = useState(0);
    const handleChipRemove = useCallback((idToRemove) => {
        onRemoveLiveboard(idToRemove);
        onSelectorSearch('');
        setSelectorResetKey(k => k + 1);
    }, [onRemoveLiveboard, onSelectorSearch]);
    const getEditButtonTooltip = () => {
        if (isClusterContext) {
            return undefined;
        }
        if (!clusterEnabled) {
            return t('adminSection.manualTranslation.clusterDisabledInfo');
        }
        return t('adminSection.manualTranslation.clusterEnabledInfo');
    };
    const editButtonTooltip = getEditButtonTooltip();
    const handleFileSelect = useCallback((file) => {
        setSelectedFile(file);
        setUploadSuccess(false);
    }, []);
    const handleDropZoneClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const handleFileInputChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [handleFileSelect]);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    }, [handleFileSelect]);
    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null);
        setUploadSuccess(false);
        onClearUploadError?.();
    }, [onClearUploadError]);
    const handleUpload = useCallback(async () => {
        if (!selectedFile || !onUploadCSV) {
            return;
        }
        const result = await onUploadCSV(selectedFile);
        if (result.success) {
            setUploadSuccess(true);
            setSelectedFile(null);
        }
    }, [selectedFile, onUploadCSV]);
    const dropZoneClassName = [
        styles.uploadDropZone,
        isDragOver ? styles.uploadDropZoneActive : '',
        uploading ? styles.uploadDropZoneDisabled : '',
    ]
        .filter(Boolean)
        .join(' ');
    const renderCsvStatusArea = () => {
        if (csvExists === null) {
            return React.createElement(LoadingIndicator, null);
        }
        if (csvExists) {
            return (React.createElement(Horizontal, { className: styles.csvStatusRow },
                React.createElement(Button, { text: downloadingExistingCsv
                        ? t('adminSection.manualTranslation.downloading')
                        : t('adminSection.manualTranslation.downloadCsvButton'), type: "secondary", onClick: onDownloadExistingCsv, isDisabled: downloadingExistingCsv || uploading || deleting, "data-testid": "download-existing-csv-button" }),
                React.createElement(Button, { text: uploading
                        ? t('adminSection.manualTranslation.uploading')
                        : t('adminSection.manualTranslation.replaceButton'), type: "secondary", onClick: uploading
                        ? undefined
                        : () => {
                            setUploadSuccess(false);
                            onReplaceCsvClick?.();
                            handleDropZoneClick();
                        }, isDisabled: uploading || downloadingExistingCsv || deleting, "data-testid": "replace-csv-button" }),
                React.createElement(Button, { text: t('adminSection.manualTranslation.deleteButton'), type: "tertiary", onClick: deleting
                        ? undefined
                        : () => setShowDeleteConfirm(true), isDisabled: deleting || uploading || downloadingExistingCsv, "data-testid": "delete-existing-csv-button" })));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Vertical, { className: dropZoneClassName, onClick: uploading ? undefined : handleDropZoneClick, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: uploading ? undefined : handleDrop, "data-testid": "manual-translation-drop-zone", role: "button", tabIndex: 0, onKeyDown: e => {
                    if (!uploading && (e.key === 'Enter' || e.key === ' ')) {
                        handleDropZoneClick();
                    }
                } },
                React.createElement(Typography, { variant: "p", className: styles.uploadDropZoneText }, t('adminSection.manualTranslation.dropZoneText')),
                React.createElement(Typography, { variant: "p", className: styles.uploadConstraints }, t('adminSection.manualTranslation.uploadConstraints')))));
    };
    if (isEditing) {
        return (React.createElement(Vertical, { className: styles.editContainer },
            React.createElement(Vertical, { className: styles.editHeader },
                React.createElement(Typography, { role: "heading", "aria-level": 2, variant: "p-large-bold", className: styles.title }, t('adminSection.manualTranslation.title')),
                React.createElement(Typography, { variant: "p", className: styles.description },
                    t('adminSection.manualTranslation.description'),
                    ' ',
                    React.createElement(Link, { href: learnMoreUrl, target: "_blank" }, t('adminSection.manualTranslation.learnMore')))),
            React.createElement(Horizontal, { className: styles.editFormRow },
                React.createElement(Typography, { variant: "p", className: styles.editFormLabel }, t('adminSection.manualTranslation.status')),
                React.createElement(Toggle, { "data-testid": "manual-translation-toggle", label: pendingValue
                        ? t('adminSection.manualTranslation.enabled')
                        : t('adminSection.manualTranslation.disabled'), checked: pendingValue, onChange: e => onToggleChange(e.target.checked), disabled: !canModify || saving, labelPosition: "right" })),
            pendingValue && onUploadCSV && (React.createElement(Vertical, { className: styles.uploadSection, "data-testid": "manual-translation-upload-section" },
                React.createElement(Typography, { variant: "p", className: styles.uploadSectionLabel }, t('adminSection.manualTranslation.csvFileLabel')),
                React.createElement(Typography, { variant: "p", className: styles.uploadDescription }, t('adminSection.manualTranslation.uploadDescription')),
                React.createElement("input", { ref: fileInputRef, type: "file", accept: ".csv", onChange: handleFileInputChange, style: { display: 'none' }, "data-testid": "manual-translation-file-input" }),
                renderCsvStatusArea(),
                selectedFile && (React.createElement(Horizontal, { className: styles.selectedFile },
                    React.createElement(Typography, { variant: "p", className: styles.selectedFileName }, selectedFile.name),
                    React.createElement(Button, { text: t('adminSection.manualTranslation.removeFile'), type: "tertiary", onClick: handleRemoveFile, isDisabled: uploading, className: styles.clearButton }))),
                selectedFile && (React.createElement(Horizontal, { className: styles.uploadActionsRow },
                    React.createElement(Button, { text: uploadButtonText, onClick: handleUpload, isDisabled: uploading, "data-testid": "manual-translation-upload-button" }))),
                React.createElement(Alert.Banner, { isVisible: !!existingCsvDownloadError, message: existingCsvDownloadError || '', type: BannerAlertType.ERROR, showCloseButton: false }),
                React.createElement(Alert.Banner, { isVisible: !!deleteError, message: deleteError || '', type: BannerAlertType.ERROR, showCloseButton: true, onClose: onClearDeleteError }),
                React.createElement(Alert.Banner, { isVisible: !!uploadError, message: uploadError || '', type: BannerAlertType.ERROR, showCloseButton: true, onClose: onClearUploadError }),
                React.createElement(Alert.Banner, { isVisible: uploadSuccess, message: t('adminSection.manualTranslation.uploadSuccess'), type: BannerAlertType.SUCCESS, showCloseButton: true, onClose: () => setUploadSuccess(false) }))),
            pendingValue && (React.createElement(Vertical, { className: styles.downloadSection },
                React.createElement(Typography, { variant: "p", className: styles.downloadSectionLabel }, t('adminSection.manualTranslation.downloadTranslationsLabel')),
                React.createElement(Typography, { variant: "p", className: styles.downloadDescription }, t('adminSection.manualTranslation.downloadDescription')),
                React.createElement(Vertical, { className: styles.selectorContainer },
                    React.createElement(LiveboardSelector, { key: selectorResetKey, onSelect: onSelectLiveboards, selectedOptionIds: selectedLiveboards.map(lb => lb.id), disabled: downloading, options: availableLiveboards, loading: selectorLoading, onSearchInput: onSelectorSearch }),
                    selectedLiveboards.length > 0 && (React.createElement(Vertical, { className: styles.selectedChipsList }, selectedLiveboards.map(lb => (React.createElement(Horizontal, { key: lb.id, className: styles.selectedChip },
                        React.createElement(Typography, { variant: "p", className: styles.selectedName }, lb.name),
                        React.createElement(Button, { text: "\u00D7", type: "tertiary", onClick: () => handleChipRemove(lb.id), className: styles.clearButton, isDisabled: downloading }))))))),
                React.createElement(Button, { "data-testid": "download-translations-button", text: downloading
                        ? t('adminSection.manualTranslation.downloading')
                        : t('adminSection.manualTranslation.downloadButton'), type: "secondary", onClick: onDownload, isDisabled: downloadDisabled }),
                React.createElement(Alert.Banner, { isVisible: !!downloadError, message: downloadError || '', type: BannerAlertType.ERROR, showCloseButton: false }))),
            React.createElement(Alert.Banner, { isVisible: !!saveError, message: saveError || '', type: BannerAlertType.ERROR, showCloseButton: false }),
            React.createElement(Horizontal, { className: styles.editActionsRow },
                React.createElement(Button, { text: t('adminSection.manualTranslation.saveConfig'), onClick: onSave, isDisabled: saveDisabled }),
                React.createElement(Button, { text: t('adminSection.manualTranslation.cancelConfig'), type: "secondary", onClick: onCancel, isDisabled: saving })),
            React.createElement(Modal, { isOpen: showDeleteConfirm, onDismiss: () => setShowDeleteConfirm(false), size: ModalSize.SMALL, title: t('adminSection.manualTranslation.deleteConfirmTitle'), dismissButton: {
                    text: t('adminSection.manualTranslation.cancelConfig'),
                    onClick: () => setShowDeleteConfirm(false),
                }, confirmButton: {
                    text: t('adminSection.manualTranslation.deleteButton'),
                    onConfirm: () => {
                        setShowDeleteConfirm(false);
                        setUploadSuccess(false);
                        onDeleteCsv?.();
                    },
                }, "data-testid": "delete-csv-confirm-modal" },
                React.createElement(Typography, { variant: "p", noMargin: true }, t('adminSection.manualTranslation.deleteConfirmBody')))));
    }
    return (React.createElement(Vertical, { className: styles.settingContainer },
        React.createElement(Horizontal, { className: styles.headerRow },
            React.createElement(Vertical, null,
                React.createElement(Typography, { role: "heading", "aria-level": 2, variant: "p-large-bold", className: styles.title }, t('adminSection.manualTranslation.title')),
                React.createElement(Typography, { variant: "p", className: styles.description },
                    t('adminSection.manualTranslation.description'),
                    ' ',
                    React.createElement(Link, { href: learnMoreUrl, target: "_blank" }, t('adminSection.manualTranslation.learnMore')))),
            React.createElement(Button, { text: t('adminSection.manualTranslation.editConfig'), type: "secondary", onClick: onEdit, isDisabled: editDisabled, "data-tooltip-content": editButtonTooltip, "data-tooltip-align": "center", "data-tooltip-position": "bottom" })),
        React.createElement(Vertical, { className: styles.settingContent },
            React.createElement(Horizontal, { className: styles.statusRow },
                React.createElement(Typography, { variant: "p", className: styles.statusLabel }, t('adminSection.manualTranslation.status')),
                React.createElement(Typography, { variant: "p", className: styles.statusValue }, enabled
                    ? t('adminSection.manualTranslation.enabled')
                    : t('adminSection.manualTranslation.disabled'))))));
};
//# sourceMappingURL=manual-translation-toggle.js.map